import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { ConnectionStatus, WebSocketContext } from "./WebSocketContext";
import {
  kMessageTypeChatJoin,
  kMessageTypeChatLeave,
  kMessageTypeMessageRead,
  kMessageTypePong,
  kMessageTypeTyping,
  WebSocketMessage,
} from "./WebSocketMessage";
import config from "../../../config/config";
import useCachedProfile from "../../../hooks/useCachedProfile";
import { useAuth } from "../../auth/hooks/useAuth";
import Cookies from "js-cookie";
import { ACTIVE_CHAT_KEY } from "../consts";
import tokenService from "../../../services/tokenService";

const setAuthCookie = (token: string) => {
  // TODO: Since we are now setting a secure cookie from server in authentication it can be evaluated to use it instead a custom one 
  Cookies.set("authToken", token, {
    secure: true,
    sameSite: "strict",
    path: "/",
    domain: config.domain,
  });
};

const globalWebSocketState: {
  instance: WebSocket | null;
  isConnecting: boolean;
  lastConnectAttempt: number;
} = {
  instance: null,
  isConnecting: false,
  lastConnectAttempt: 0,
};

const reconnectionState = {
  lastDisconnectTime: 0,
  reconnectionAttemptTimeout: null as NodeJS.Timeout | null,
  inReconnectionCooldown: false,
  activeConnections: 0,
};

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.CLOSED
  );
  const messageHandlers = useRef<Set<(message: WebSocketMessage) => void>>(
    new Set()
  );
  const { isAuthenticated, accessToken } = useAuth();
  const { user } = useCachedProfile();
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const connectRef = useRef<() => void>(() => {});

  const isReconnecting = useRef(false);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPongTimeRef = useRef<number>(0);

  const sendMessage = useCallback(
    (message: any) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(message));
      }
    },
    [socket]
  );

  const connect = useCallback(async () => {
    const now = Date.now();
    if (globalWebSocketState.isConnecting) {
      return;
    }

    // Work with a local token snapshot to avoid races with the hook state
    let tokenToUse = accessToken;

    // If we don't have a token or it's invalid, try to refresh first (single-flight)
    if (!tokenToUse || !tokenService.isTokenValid(tokenToUse)) {

      // If a refresh is already in-flight, wait for it. Otherwise trigger a refresh via the auth layer.
      if (!tokenService.getRefreshPromise()) {
        // Trigger the auth provider to start a refresh (use existing handler)
        window.dispatchEvent(new CustomEvent("force-token-refresh"));
      }

      const refreshResult = await tokenService.waitForRefreshIfAny();

      if (!refreshResult) {
        return;
      }

      // Read the updated token from in-memory cache or storage
      tokenToUse = tokenService.getAccessTokenSync() || (await tokenService.getAccessToken());

      if (!tokenToUse) {
        return;
      }
    }

    // Enforce a strict cooldown period between reconnection attempts
    // This is CRITICAL on mobile - too frequent reconnects can cause browser throttling
    const timeSinceLastDisconnect = now - reconnectionState.lastDisconnectTime;
    if (
      reconnectionState.inReconnectionCooldown ||
      timeSinceLastDisconnect < 2000
    ) {
      // Schedule a future reconnection attempt
      if (!reconnectionState.reconnectionAttemptTimeout) {
        reconnectionState.reconnectionAttemptTimeout = setTimeout(() => {
          reconnectionState.inReconnectionCooldown = false;
          reconnectionState.reconnectionAttemptTimeout = null;
          connect();
        }, Math.max(2000 - timeSinceLastDisconnect, 1000));
      }
      return;
    }

    if (now - globalWebSocketState.lastConnectAttempt < 2000) {
      return;
    }

    if (!isAuthenticated || !user || !tokenToUse) {
      return;
    }

    // Use the global socket if it's already connected
    if (globalWebSocketState.instance?.readyState === WebSocket.OPEN) {
      setSocket(globalWebSocketState.instance);
      setConnectionStatus(ConnectionStatus.OPEN);
      return;
    }

    // Close existing socket if any
    if (socket) {
      try {
        socket.close(1000, "Reconnecting");
      } catch (e) {
        console.error("Error closing socket:", e);
      }
      setSocket(null);
    }

    globalWebSocketState.isConnecting = true;
    globalWebSocketState.lastConnectAttempt = now;

    try {
      // Use the refreshed/stable token when creating the socket and cookie
      setAuthCookie(tokenToUse as string);
      const wsUrl = new URL(`${config.api.websocket}/chat/${user.userId}`);
      const ws = new WebSocket(wsUrl);
      setConnectionStatus(ConnectionStatus.CONNECTING);

      ws.onopen = () => {
        setConnectionStatus(ConnectionStatus.OPEN);
        reconnectAttempts.current = 0; // Reset on successful connection
        isReconnecting.current = false;

        // Reset the last pong time on connection
        lastPongTimeRef.current = Date.now();

        // Set up ping interval - only on mobile devices
        if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }

          pingIntervalRef.current = setInterval(() => {
            if (!ws || ws.readyState !== WebSocket.OPEN) return;

            // If no message received for 60 seconds, reconnect (very lenient)
            if (Date.now() - lastPongTimeRef.current > 60000) {
              try {
                ws.close(1000, "Inactivity timeout");
              } catch (e) {
                console.error("Error closing socket:", e);
              }

              // Force reset connection state
              globalWebSocketState.instance = null;
              globalWebSocketState.isConnecting = false;
              reconnectionState.inReconnectionCooldown = false;

              connect();
              return;
            }

            // Generate a unique correlation ID for this ping
            const correlationId = `ping-${Date.now()}`;

            // Create a one-time ping-specific handler
            const pingHandler = (message: WebSocketMessage) => {
              if (
                message.type === kMessageTypePong &&
                message.data?.correlationId === correlationId
              ) {
                messageHandlers.current.delete(pingHandler);
              }
            };

            // Send a ping message
            try {
              sendMessage({
                type: "ping",
                version: 1,
                source: "web",
                data: {
                  timestamp: Date.now(),
                  correlationId: correlationId,
                },
              });

              setTimeout(() => {
                messageHandlers.current.delete(pingHandler);
                // Only force reconnect if we haven't received ANY message for 30 seconds
                // This is more lenient and prevents unnecessary reconnections
                if (
                  ws &&
                  ws.readyState === WebSocket.OPEN &&
                  Date.now() - lastPongTimeRef.current > 30000
                ) {
                  try {
                    ws.close(1000, "Connection timeout");
                  } catch (e) {
                    console.error("Error closing socket:", e);
                  }
                  reconnectionState.inReconnectionCooldown = false;
                  connect();
                }
              }, 10000); // Wait 10 seconds for ping response instead of 5
            } catch (e) {
              console.error("Error sending ping:", e);
            }
          }, 30000); // Every 30 seconds instead of 15
        }

        globalWebSocketState.instance = ws;
        globalWebSocketState.isConnecting = false;
      };

      ws.onclose = (event) => {
        setConnectionStatus(ConnectionStatus.CLOSED);
        setSocket(null);
        isReconnecting.current = false;

        // Record disconnect time for cooldown period
        reconnectionState.lastDisconnectTime = Date.now();

        // Set cooldown flag to prevent immediate reconnection
        reconnectionState.inReconnectionCooldown = true;

        // Check if closure might be due to token expiration (code 1008 or 401)
        if (
          (event.code === 1008 || event.code === 4401) &&
          document.visibilityState === "visible"
        ) {
          if (!tokenService.isTokenValid(accessToken)) {
            window.dispatchEvent(new Event("auth-change"));
            return;
          }
        }

        // Only auto-reconnect if it wasn't a clean closure
        if (event.code !== 1000 && document.visibilityState === "visible") {
          // Schedule reconnect with exponential backoff
          const delay = Math.min(
            2000 + 1000 * Math.pow(1.5, reconnectAttempts.current),
            30000
          );

          if (reconnectTimer.current) {
            clearTimeout(reconnectTimer.current);
          }

          reconnectTimer.current = setTimeout(() => {
            reconnectAttempts.current += 1;
            reconnectionState.inReconnectionCooldown = false;
            connectRef.current();
          }, delay);
        }

        globalWebSocketState.instance = null;
        globalWebSocketState.isConnecting = false;
      };

      ws.onerror = (error) => {
        console.error("WebSocket Error:", error);
        setConnectionStatus(ConnectionStatus.ERROR);
        isReconnecting.current = false;
        globalWebSocketState.isConnecting = false;
      };

      ws.onmessage = (event) => {
        // Any message from the server is treated as a pong
        lastPongTimeRef.current = Date.now();
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          messageHandlers.current.forEach((handler) => handler(message));
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };
      setSocket(ws);
    } catch (error) {
      console.error("Error creating WebSocket:", error);
      isReconnecting.current = false;
      globalWebSocketState.isConnecting = false;
    }
  }, [accessToken, isAuthenticated, sendMessage, socket, user]);

  useEffect(() => {
    const handleAuthChange = () => {
      if (
        isAuthenticated &&
        accessToken &&
        tokenService.isTokenValid(accessToken)
      ) {
        // Only reconnect if we have a valid token and previous connection was broken
        if (
          connectionStatus === ConnectionStatus.CLOSED ||
          connectionStatus === ConnectionStatus.ERROR
        ) {
          reconnectionState.inReconnectionCooldown = false;
          connect();
        }
      }
    };

    window.addEventListener("auth-change", handleAuthChange);
    return () => {
      window.removeEventListener("auth-change", handleAuthChange);
    };
  }, [accessToken, isAuthenticated, connectionStatus, connect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, []);

  const sendJoin = useCallback(
    (chatId: string) => {
      sendMessage({
        type: kMessageTypeChatJoin,
        version: 1,
        source: "web",
        data: { chatId },
      });
    },
    [sendMessage]
  );

  const lastActiveTimeRef = useRef<number>(Date.now());

  // Handle document visibility changes
  useEffect(() => {
    let focusTimeout: NodeJS.Timeout | null = null;
    let pingResponseTimeout: NodeJS.Timeout | null = null;
    let visibilityPingReceived = false;
    let lastVisibilityCheck = 0;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Prevent excessive visibility checks
        const now = Date.now();
        if (now - lastVisibilityCheck < 5000) {
          return;
        }
        lastVisibilityCheck = now;

        const timeInBackground = now - lastActiveTimeRef.current;
        lastActiveTimeRef.current = now;

        // Clear any existing timeouts
        if (focusTimeout) clearTimeout(focusTimeout);
        if (pingResponseTimeout) clearTimeout(pingResponseTimeout);

        focusTimeout = setTimeout(() => {
          // Case 1: Socket appears OPEN - Only verify if inactive for significant time
          if (socket?.readyState === WebSocket.OPEN) {
            // Only trigger sync and verification if inactive for more than 30 seconds
            if (timeInBackground > 30000) {
              // Instead of changing connection status (which causes re-renders),
              // dispatch a custom event that components can listen to if needed
              window.dispatchEvent(new CustomEvent('ws-visibility-check', {
                detail: { inactiveTime: timeInBackground }
              }));

              // Verify the socket is truly alive with ping/pong
              const correlationId = `vis-${Date.now()}`;
              visibilityPingReceived = false;

              const pingHandler = (message: WebSocketMessage) => {
                if (
                  message.type === kMessageTypePong &&
                  message.data?.correlationId === correlationId
                ) {
                  visibilityPingReceived = true;
                  messageHandlers.current.delete(pingHandler);
                }
              };

              messageHandlers.current.add(pingHandler);

              try {
                sendMessage({
                  type: "ping",
                  version: 1,
                  source: "web",
                  data: {
                    timestamp: now,
                    correlationId: correlationId,
                  },
                });

                pingResponseTimeout = setTimeout(() => {
                  messageHandlers.current.delete(pingHandler);

                  if (!visibilityPingReceived) {
                    if (socket) {
                      try {
                        socket.close(1000, "Dead connection detected");
                      } catch (e) {
                        console.error("Error closing socket:", e);
                      }
                    }

                    reconnectionState.inReconnectionCooldown = false;
                    connect();
                    restoreActiveChat();
                  }
                }, 5000); // 5 seconds to wait for response
              } catch (e) {
                connect();
              }
            }
          }
          // Case 2: Socket is not OPEN - Reconnect immediately
          else {
            reconnectionState.inReconnectionCooldown = false;

            connect();
            restoreActiveChat();
          }
        }, 500);
      } else {
        lastActiveTimeRef.current = Date.now();
      }
    };

    // Helper function to restore active chat
    const restoreActiveChat = () => {
      const activeChatId = localStorage.getItem(ACTIVE_CHAT_KEY);
      if (activeChatId) {
        setTimeout(() => {
          if (connectionStatus === ConnectionStatus.OPEN) {
            sendJoin(activeChatId);
          }
        }, 1000);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (focusTimeout) clearTimeout(focusTimeout);
      if (pingResponseTimeout) clearTimeout(pingResponseTimeout);
    };
  }, [socket, connect, sendJoin, sendMessage, connectionStatus]);

  const disconnect = useCallback(
    (preserveState = false) => {
      if (socket) {
        // Don't manage active chat here anymore
        try {
          socket.close(1000, "Closing normally");
        } catch (e) {
          console.error("Error closing socket:", e);
        }
        setSocket(null);
      }
    },
    [socket]
  );

  const sendTyping = useCallback(
    (chatId: string, typing: boolean) => {
      sendMessage({
        type: kMessageTypeTyping,
        version: 1,
        source: "web",
        data: { chatId, typing },
      });
    },
    [sendMessage]
  );

  const sendLeave = useCallback(
    (chatId: string) => {
      sendMessage({
        type: kMessageTypeChatLeave,
        version: 1,
        source: "web",
        data: { chatId },
      });
    },
    [sendMessage]
  );

  const sendReadReceipt = useCallback(
    (chatId: string, messageIds: string[]) => {
      if (!messageIds.length) return;

      sendMessage({
        type: kMessageTypeMessageRead,
        version: 1,
        source: "web",
        data: {
          chatId,
          messageIds,
          readAt: new Date().toISOString(),
        },
      });
    },
    [sendMessage]
  );

  const onMessage = useCallback(
    (handler: (message: WebSocketMessage) => void) => {
      messageHandlers.current.add(handler);
    },
    []
  );

  const removeMessageHandler = useCallback(
    (handler: (message: WebSocketMessage) => void) => {
      messageHandlers.current.delete(handler);
    },
    []
  );

  useEffect(() => {
    if (isAuthenticated && user && accessToken) {
      connect();
    } else {
      disconnect(false);
    }

    // Don't disconnect on every unmount, only when auth changes
    return () => {
      // Only disconnect if we're actually logging out or the app is completely closing
      if (!document.hidden && (!isAuthenticated || !user || !accessToken)) {
        disconnect(false);
      }
    };
  }, [isAuthenticated, user, accessToken, connect, disconnect]);

  const contextValue = useMemo(
    () => ({
      sendTyping,
      sendJoin,
      sendLeave,
      sendReadReceipt,
      onMessage,
      removeMessageHandler,
      connectionStatus,
      connect,
      disconnect,
    }),
    [
      sendTyping,
      sendJoin,
      sendLeave,
      sendReadReceipt,
      onMessage,
      removeMessageHandler,
      connectionStatus,
      connect,
      disconnect,
    ]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
